import * as React from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { UsPhoneInput } from "@/components/ui/phone-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ReferencePicker, type ReferenceChoice } from "@/features/reference-picker";
import { type Draft, editable, latestDay, maxLength, referenceText } from "@/lib/record-fields";
import type { FormEntity, RecordField } from "@/lib/types";

/*
 * A record field's input, by its type, for the Phone page's call form. The record's own details panel
 * (the contact page and the Conversations screen, `features/record-details.tsx`) changes a field where it
 * is read and draws its own inputs; the cards and the drawer that were here gave way to it (ACL-455).
 */

/** Why a field cannot be changed, or nothing when it can. */
function lockedHint(field: RecordField): string | undefined {
  if (!editable(field)) return "Your role can read this field, not change it.";
  return undefined;
}

const NOT_SET = "__not_set__";

/** One field's input, by its type. Also drawn by the Phone page's form (`softphone/call-form.tsx`),
 *  which gives its inputs another `idPrefix` so the two never share an id on one screen. */
export function FieldControl({
  entity, field, draft, pickedLabel, problem, notice, onChange, onPick, idPrefix = "contact-field", className,
}: {
  entity: FormEntity;
  field: RecordField;
  draft: Draft;
  /** A reference's name, once picked in this drawer. */
  pickedLabel?: string;
  problem?: string;
  /** Why the answer is wanted now, in the warning tone in place of the hint: what Send contract on the
   *  call needs (ACL-560). A `problem` still says itself over it. */
  notice?: string | null;
  onChange: (draft: Draft) => void;
  onPick: (choice: ReferenceChoice | null) => void;
  idPrefix?: string;
  /** On the field's block: the call form lays its label, input and hint on the rows of its grid. */
  className?: string;
}) {
  const id = `${idPrefix}-${field.key}`;
  const locked = !editable(field);
  const hint = notice ? <ContractNotice text={notice} /> : (lockedHint(field) ?? typeHint(field));
  const text = Array.isArray(draft) ? "" : draft;
  const input = (props: React.ComponentProps<typeof Input>) => (
    <Input
      value={text}
      disabled={locked}
      invalid={Boolean(problem)}
      autoComplete="off"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      {...props}
    />
  );

  let control: React.ReactNode;
  switch (field.type) {
    case "long_text":
      control = (
        <Textarea
          value={text}
          rows={4}
          disabled={locked}
          invalid={Boolean(problem)}
          maxLength={maxLength(field)}
          className="font-sans text-sm"
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "number":
      control = input({ inputMode: "decimal" });
      break;
    case "email":
      control = input({ type: "email", maxLength: maxLength(field) });
      break;
    case "phone":
      // The +1 is fixed; the rep types the ten digits after it (ACL-653).
      control = <UsPhoneInput id={id} value={text} disabled={locked} invalid={Boolean(problem)} onChange={onChange} />;
      break;
    case "date": {
      // An accident or a birthday stops at today (ACL-577); any other date takes any day.
      const latest = latestDay(field);
      control = (
        <DatePicker
          id={id}
          value={text}
          max={latest}
          disabled={locked}
          invalid={Boolean(problem)}
          clearable={!field.required}
          onChange={onChange}
        />
      );
      break;
    }
    case "datetime":
      control = input({ type: "datetime-local" });
      break;
    case "boolean":
    case "dropdown": {
      const options =
        field.type === "boolean"
          ? [{ key: "yes", label: "Yes" }, { key: "no", label: "No" }]
          : (field.config.options ?? []);
      // A stored key an admin has since taken off the list stays pickable, so opening and saving
      // the drawer does not quietly change it.
      const gone = text && !options.some((o) => o.key === text) ? [{ key: text, label: text }] : [];
      control = (
        <Select value={text || NOT_SET} onValueChange={(v) => onChange(v === NOT_SET ? "" : v)} disabled={locked}>
          <SelectTrigger id={id} aria-label={field.title} invalid={Boolean(problem)}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NOT_SET}>Not set</SelectItem>
            {[...options, ...gone].map((o) => (
              <SelectItem key={o.key} value={o.key}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    }
    case "multi_select": {
      const picked = Array.isArray(draft) ? draft : [];
      control = (
        <div
          role="group"
          aria-labelledby={`${id}-label`}
          className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-text"
        >
          {(field.config.options ?? []).map((o) => (
            <label key={o.key} className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                className="accent-accent-9"
                disabled={locked}
                checked={picked.includes(o.key)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...picked, o.key] : picked.filter((k) => k !== o.key))
                }
              />
              {o.label}
            </label>
          ))}
        </div>
      );
      break;
    }
    case "reference": {
      // The draft is the id; what it reads as is the name picked here, else the stored one's.
      const chosen: ReferenceChoice | null = text
        ? { id: text, label: pickedLabel || (String(field.value ?? "") === text ? referenceText(field) : null) || text }
        : null;
      control = locked ? (
        input({ value: chosen?.label ?? "", placeholder: "Not set" })
      ) : (
        <ReferencePicker
          entity={entity}
          fieldKey={field.key}
          target={field.config.target}
          value={chosen}
          onChange={onPick}
          allowNone={!(field.required && field.value !== null && field.value !== undefined)}
          invalid={Boolean(problem)}
        />
      );
      break;
    }
    default:
      control = input({ maxLength: maxLength(field) });
  }

  return (
    <Field label={field.title} htmlFor={id} required={field.required} hint={hint} error={problem} className={className}>
      {control}
    </Field>
  );
}

/** The sentence under a field Send contract needs (ACL-541's words, in its warning tone). */
export function ContractNotice({ text }: { text: string }) {
  return <span className="font-medium text-warning-11">{text}</span>;
}

/** A short line under an editable field when its type has a rule worth knowing before saving. */
function typeHint(field: RecordField): string | undefined {
  const c = field.config;
  if (field.type === "number") {
    const range =
      c.min !== undefined && c.max !== undefined ? `From ${c.min} to ${c.max}`
        : c.min !== undefined ? `At least ${c.min}`
          : c.max !== undefined ? `At most ${c.max}`
            : null;
    const places = c.decimals === 0 ? "a whole number" : c.decimals !== undefined ? `up to ${c.decimals} decimal place${c.decimals === 1 ? "" : "s"}` : null;
    const parts = [range, places].filter(Boolean) as string[];
    if (parts.length === 0) return undefined;
    const line = parts.join(", ");
    return `${line.charAt(0).toUpperCase()}${line.slice(1)}.`;
  }
  if (field.type === "datetime") return "In your own time zone.";
  return undefined;
}
